import { ILLUSTRATIONS, Illustration, Show, Tooltip } from '#components'
import { useCtas, useMeta } from '#hooks'
import {
  clickUpgradeAtSidebarAdvancedEvent,
  viewSidebarTabAdvancedEvent,
  viewUpgradeAtSidebarAdvancedEvent,
} from '#services/events'
import { sendMetaTags } from '#services/metaTags'
import {
  Button,
  ButtonGroup,
  Flex,
  FormToggle,
  Icon,
  Modal,
  PanelBody,
  Spinner,
  TextControl,
} from '@wordpress/components'
import { useEffect, useState } from '@wordpress/element'
import { helpFilled } from '@wordpress/icons'
import { useController, useForm } from 'react-hook-form'
import { Trans as T, useTranslation } from 'react-i18next'

import './styles.css'

const PanelBodyWithIcon = ({ title, children, tooltip }) => (
  <PanelBody
    title={
      <>
        {title}
        <Tooltip height={18} text={tooltip}>
          <Icon className="advanced__icon" size={18} icon={helpFilled} />
        </Tooltip>
      </>
    }
  >
    {children}
  </PanelBody>
)

const Switch = ({ children }) => (
  <Flex justify="flex-start">
    <span>
      <T i18nKey="g.no" />
    </span>
    {children}
    <span>
      <T i18nKey="g.yes" />
    </span>
  </Flex>
)

export const Advanced = ({ pop }) => {
  const { t } = useTranslation()
  const [showModal, setShowModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { data: availableCtas, isLoading: isLoadingCTA } = useCtas()
  const upgradeCta = availableCtas?.find(
    (cta) => cta.id === 'wordpress-upgrade',
  )

  const {
    data: noindex,
    isLoading: isLoadingNoindex,
    isValidating: isValidatingNoindex,
    send: sendNoindex
  } = useMeta({
    popId: pop.id,
    key: 'noindex',
  })

  const {
    data: redirection,
    isLoading: isLoadingRedirection,
    isValidating: isValidatingRedirection,
    send: sendRedirection
  } = useMeta({
    popId: pop.id,
    key: 'redirection',
  })

  const {
    data: canonical,
    isLoading: isLoadingCanonical,
    isValidating: isValidatingCanonical,
    send: sendCanonical
  } = useMeta({
    popId: pop.id,
    key: 'canonical',
  })

  const { control, handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      noindex: false,
      redirection: false,
      redirectionUrl: '',
      canonical: false,
      canonicalUrl: '',
    },
    values: {
      noindex: Boolean(noindex),
      redirection: Boolean(redirection),
      redirectionUrl: redirection || '',
      canonical: Boolean(canonical),
      canonicalUrl: canonical || '',
    },
  })

  const { field: noindexField } = useController({
    name: 'noindex',
    control,
  })

  const { field: redirectionField } = useController({
    name: 'redirection',
    control,
  })

  const { field: redirectionUrlField } = useController({
    name: 'redirectionUrl',
    control,
    shouldUnregister: true,
    rules: { 
      required: watch('redirection'),
      pattern: {
        value: /^https?:\/\/.+/,
        message: t('sidebar.advanced.redirection.error')
      }
    },
  })

  const { field: canonicalField } = useController({
    name: 'canonical',
    control,
  })

  const { field: canonicalUrlField } = useController({
    name: 'canonicalUrl',
    control,
    shouldUnregister: true,
    rules: { 
      required: watch('canonical'),
      pattern: {
        value: /^https?:\/\/.+/,
        message: t('sidebar.advanced.canonical.error')
      }
    },
  })

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true)
      
      await Promise.all([
        sendNoindex(data.noindex),
        sendRedirection(data.redirection ? data.redirectionUrl : ''),
        sendCanonical(data.canonical ? data.canonicalUrl : '')
      ])
      
      // Aquí podrías mostrar un mensaje de éxito
      console.log('All updates successful')
    } catch (error) {
      console.error('Error submitting form:', error)
      // Aquí podrías mostrar un mensaje de error al usuario
    } finally {
      setIsSubmitting(false)
    }
  }

  const restoreRedirectionUrl = () => {
    setValue('redirection', true)
    setShowModal(false)
  }

  const emptyRedirectionUrl = () => {
    setValue('redirectionUrl', '')
    setShowModal(false)
  }

  const navigateToUpgrade = () => {
    clickUpgradeAtSidebarAdvancedEvent()
    window.open(upgradeCta?.attributes.targetUrl, '_blank')
  }

  useEffect(() => {
    if (!watch('redirection') && watch('redirectionUrl') !== '') {
      setShowModal(true)
    }
  }, [watch('redirection')])

  useEffect(() => {
    viewSidebarTabAdvancedEvent()

    if (upgradeCta) {
      viewUpgradeAtSidebarAdvancedEvent()
    }
  }, [])

  return upgradeCta ? (
    <div className="advanced-lite">
      <Illustration name={ILLUSTRATIONS.UPGRADE.ROCKET} />
      <b>
        <p>{t('upgrade.panel.title')}</p>
      </b>
      <Button
        className="upgrade-btn"
        isBusy={isSubmitting}
        disabled={isSubmitting}
        onClick={navigateToUpgrade}
        variant="primary"
      >
        {t('g.upgrade')}
      </Button>
    </div>
  ) : (
    <>
      <form className="advanced" onSubmit={handleSubmit(onSubmit)}>
        <PanelBodyWithIcon
          title={t('sidebar.advanced.indexation.title')}
          tooltip={
            <T
              i18nKey="sidebar.advanced.indexation.popup"
              components={[<b />]}
            />
          }
        >
          <p>{t('sidebar.advanced.indexation.text')}</p>
          {isLoadingNoindex || isValidatingNoindex ? (
            <Spinner />
          ) : (
            <Switch>
              <FormToggle {...noindexField} checked={noindexField.value} />
            </Switch>
          )}
        </PanelBodyWithIcon>

        <PanelBodyWithIcon
          title={t('sidebar.advanced.redirection.title')}
          tooltip={
            <T
              i18nKey="sidebar.advanced.redirection.popup"
              components={[<b />]}
            />
          }
        >
          <p>{t('sidebar.advanced.redirection.text')}</p>
          {isLoadingRedirection || isValidatingRedirection ? (
            <Spinner />
          ) : (
            <Switch>
              <FormToggle
                {...redirectionField}
                checked={redirectionField.value}
              />
            </Switch>
          )}
          <Show when={watch('redirection')}>
            <TextControl
              type="url"
              {...redirectionUrlField}
              className="advanced__textfield"
              label={t('sidebar.advanced.redirection.label')}
              placeholder={t('sidebar.advanced.redirection.placeholder')}
              disabled={!watch('redirection')}
            />
          </Show>
        </PanelBodyWithIcon>

        <PanelBodyWithIcon
          title={t('sidebar.advanced.canonical.title')}
          tooltip={
            <T
              i18nKey="sidebar.advanced.canonical.popup"
              components={[<b />]}
            />
          }
        >
          <p>{t('sidebar.advanced.canonical.text')}</p>
          {isLoadingCanonical || isValidatingCanonical ? (
            <Spinner />
          ) : (
            <Switch>
              <FormToggle {...canonicalField} checked={canonicalField.value} />
            </Switch>
          )}
          <Show when={watch('canonical')}>
            <TextControl
              type="url"
              {...canonicalUrlField}
              className="advanced__textfield"
              label={t('sidebar.advanced.canonical.label')}
              placeholder={t('sidebar.advanced.canonical.placeholder')}
              disabled={!watch('canonical')}
            />
          </Show>
        </PanelBodyWithIcon>
        <Button
          isBusy={isSubmitting}
          disabled={isSubmitting}
          variant="primary"
          type="submit"
        >
          {t('g.save')}
        </Button>
      </form>
      <Show when={showModal}>
        <Modal
          className="mktseo"
          title={t('sidebar.advanced.redirection.modal.title')}
          onRequestClose={restoreRedirectionUrl}
        >
          <p>{t('sidebar.advanced.redirection.modal.text')}</p>
          <ButtonGroup>
            <Flex gap="5" justify="end">
              <Button
                isDestructive
                variant="secondary"
                onClick={restoreRedirectionUrl}
              >
                {t('g.cancel')}
              </Button>
              <Button
                isDestructive
                variant="primary"
                onRequestClose={restoreRedirectionUrl}
                onClick={emptyRedirectionUrl}
              >
                {t('sidebar.advanced.redirection.modal.button')}
              </Button>
            </Flex>
          </ButtonGroup>
        </Modal>
      </Show>
    </>
  )
}
